import { create } from 'zustand'

export const useAppUpdateStore = create(() => ({
  buildId: null, dismissed: null, recovery: false, blocked: false, applying: false, error: null
}))

const guards = new Set()
const retainedGuards = new Map()
const operations = new Set()
let registration = null
let checking = null

export const isUpdateBlocked = () => retainedGuards.size > 0 || operations.size > 0 || [...guards].some((guard) => guard())
export const refreshUpdateBlockers = () => {
  const blocked = isUpdateBlocked()
  if (useAppUpdateStore.getState().blocked !== blocked) useAppUpdateStore.setState({ blocked })
}
export const registerUpdateGuard = (guard, key = null, persist = guard) => {
  guards.add(guard)
  refreshUpdateBlockers()
  return () => {
    if (!guards.delete(guard)) return
    if (key && persist()) retainedGuards.set(key, Symbol(key))
    refreshUpdateBlockers()
  }
}
export const isUpdateOperationBlocked = (key) => [...operations].some((operation) => operation.key === key)
export const beginUpdateOperation = (key) => {
  const operation = { key, pending: true }
  operations.add(operation)
  refreshUpdateBlockers()
  return (confirmed = true) => {
    if (!operation.pending) return
    operation.pending = false
    if (confirmed) operations.delete(operation)
    refreshUpdateBlockers()
  }
}
export const prepareUpdateRecovery = (key) => {
  const retained = retainedGuards.get(key)
  const settled = [...operations].filter((operation) => operation.key === key && !operation.pending)
  return () => {
    if (retainedGuards.get(key) === retained) retainedGuards.delete(key)
    settled.forEach((operation) => operations.delete(operation))
    refreshUpdateBlockers()
  }
}
export const attachUpdateRegistration = (value) => { registration = value }

export function checkAppVersion() {
  if (checking) return checking
  checking = (async () => {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 10000)
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', signal: controller.signal })
      if (!response.ok) return null
      const data = await response.json()
      if (typeof data.buildId !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(data.buildId)) return null
      const buildId = data.buildId === import.meta.env.VITE_APP_BUILD_ID ? null : data.buildId
      const state = useAppUpdateStore.getState()
      useAppUpdateStore.setState({ buildId, ...(state.buildId !== buildId ? { error: null } : {}) })
      return data.buildId
    } catch {
      return null
    } finally {
      clearTimeout(timeout)
      checking = null
    }
  })()
  return checking
}

export function dismissAppUpdate() {
  const { buildId, recovery, applying } = useAppUpdateStore.getState()
  if (recovery || applying) return
  useAppUpdateStore.setState({ dismissed: buildId })
  try { sessionStorage.setItem('skinoz_update_dismissed', buildId) } catch { return }
}

export function notifyChunkError() {
  useAppUpdateStore.setState({ recovery: true, error: null })
  void checkAppVersion()
}

function messageWorker(worker, data) {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel()
    const finish = (error, result) => {
      clearTimeout(timeout)
      channel.port1.close()
      channel.port2.close()
      if (error) reject(error)
      else resolve(result)
    }
    const timeout = setTimeout(() => finish(new Error('failed')), 5000)
    channel.port1.onmessage = (event) => finish(null, event.data)
    try { worker.postMessage(data, [channel.port2]) } catch (error) { finish(error) }
  })
}

async function waitUntil(read, updateDeadline) {
  const deadline = Math.min(Date.now() + 15000, updateDeadline)
  while (Date.now() < deadline) {
    const value = read()
    if (value) return value
    await new Promise((resolve) => setTimeout(resolve, 100))
  }
  throw new Error('failed')
}

async function beforeUpdateDeadline(operation, deadline) {
  if (Date.now() >= deadline) throw new Error('failed')
  let timeout
  try {
    return await Promise.race([operation(), new Promise((_, reject) => {
      timeout = setTimeout(() => reject(new Error('failed')), Math.max(0, deadline - Date.now()))
    })])
  } finally {
    clearTimeout(timeout)
  }
}

export async function applyAppUpdate() {
  if (useAppUpdateStore.getState().applying || isUpdateBlocked()) return
  useAppUpdateStore.setState({ applying: true, error: null })
  const deadline = Date.now() + 30000
  const prepare = (operation) => beforeUpdateDeadline(operation, deadline)
  let reloading = false
  try {
    const target = await prepare(checkAppVersion)
    if (!target) throw new Error('failed')
    if (isUpdateBlocked() || target === import.meta.env.VITE_APP_BUILD_ID && !useAppUpdateStore.getState().recovery) return
    if (navigator.serviceWorker?.controller) {
      registration ||= await prepare(() => navigator.serviceWorker.getRegistration())
      if (!registration) throw new Error('failed')
      await prepare(() => registration.update())
      const worker = await prepare(() => waitUntil(() => registration.waiting || (!registration.installing && registration.active), deadline))
      const info = await prepare(() => messageWorker(worker, { type: 'GET_BUILD_ID' }))
      if (info.buildId !== target) throw new Error('failed')
      if (isUpdateBlocked()) return
      if (worker !== navigator.serviceWorker.controller) {
        const result = await prepare(() => messageWorker(worker, { type: 'APPLY_UPDATE', buildId: target }))
        if (result.error) throw new Error(result.error)
        await prepare(() => waitUntil(() => navigator.serviceWorker.controller === worker, deadline))
      }
    }
    if (Date.now() >= deadline) throw new Error('failed')
    if (!isUpdateBlocked()) { window.location.reload(); reloading = true }
  } catch (error) {
    useAppUpdateStore.setState({ error: error.message === 'otherTabs' ? 'otherTabs' : 'failed' })
  } finally {
    if (!reloading) useAppUpdateStore.setState({ applying: false })
    refreshUpdateBlockers()
  }
}

export function startAppUpdates() {
  if (import.meta.env.DEV) return () => {}
  try { useAppUpdateStore.setState({ dismissed: sessionStorage.getItem('skinoz_update_dismissed') }) } catch { useAppUpdateStore.setState({ dismissed: null }) }
  const check = () => {
    if (document.visibilityState !== 'visible') return
    void checkAppVersion()
    registration?.update().catch(() => {})
  }
  const preloadError = (event) => { event.preventDefault(); notifyChunkError() }
  const interval = setInterval(check, 60000)
  document.addEventListener('visibilitychange', check)
  window.addEventListener('online', check)
  window.addEventListener('vite:preloadError', preloadError)
  check()
  return () => {
    clearInterval(interval)
    document.removeEventListener('visibilitychange', check)
    window.removeEventListener('online', check)
    window.removeEventListener('vite:preloadError', preloadError)
  }
}
